import React from 'react';

/** Opaque mention token stored in comment text: @[userId] */
export const MENTION_TOKEN_REGEX = /@\[([^\]]+)\]/g;

export interface MentionMeta {
  id: string;
  name: string;
  email?: string;
}

export const buildMentionToken = (userId: string): string => `@[${userId}]`;

const buildMentionLookup = (
  mentions: MentionMeta[] = [],
  users: Array<{id: string; name: string; email?: string}> = []
): Map<string, MentionMeta> => {
  const mentionById = new Map<string, MentionMeta>();
  mentions.forEach((m) => mentionById.set(m.id, m));
  users.forEach((u) => {
    if (!mentionById.has(u.id)) {
      mentionById.set(u.id, {id: u.id, name: u.name, email: u.email});
    }
  });

  return mentionById;
};

/** Convert stored @[userId] tokens to @DisplayName for the composer textarea. */
export const formatMentionsForDisplay = (
  text: string,
  mentions: MentionMeta[] = [],
  users: Array<{id: string; name: string; email?: string}> = []
): string => {
  if (!text) return text;

  const mentionById = buildMentionLookup(mentions, users);

  return text.replace(new RegExp(MENTION_TOKEN_REGEX.source, 'g'), (_, userId) => {
    const meta = mentionById.get(userId);

    return `@${meta?.name ?? userId}`;
  });
};

/** Convert @DisplayName labels in composer text back to @[userId] tokens for storage. */
export const encodeMentionsInText = (
  text: string,
  mentions: MentionMeta[] = []
): string => {
  if (!text || !mentions.length) return text;

  let result = text;
  const sorted = [...mentions].sort((a, b) => b.name.length - a.name.length);

  for (const mention of sorted) {
    const token = buildMentionToken(mention.id);
    if (result.includes(token)) continue;

    const escapedName = mention.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    result = result.replace(
      new RegExp(`@${escapedName}(?=\\s|$|[.,!?;:])`, 'g'),
      token
    );
  }

  return result;
};

export const insertMentionAtCursor = (
  text: string,
  cursorPosition: number,
  displayName: string
): {text: string; cursorAfter: number} => {
  const textBeforeCursor = text.slice(0, cursorPosition);
  const lastAtSymbol = textBeforeCursor.lastIndexOf('@');
  const textAfterCursor = text.slice(cursorPosition);
  const mentionLabel = `@${displayName}`;
  const atIndex = lastAtSymbol >= 0 ? lastAtSymbol : cursorPosition;
  const newText = text.slice(0, atIndex) + `${mentionLabel} ` + textAfterCursor;
  const cursorAfter = atIndex + mentionLabel.length + 1;

  return {text: newText, cursorAfter};
};

export const renderTextWithMentions = (
  text: string,
  mentions: MentionMeta[] = [],
  users: Array<{id: string; name: string; email?: string}> = []
): React.ReactNode[] | null => {
  if (!text) return null;

  const hasTokenMentions = new RegExp(MENTION_TOKEN_REGEX.source).test(text);
  if (!hasTokenMentions) {
    return renderLegacyNameMentions(text, mentions, users);
  }

  const mentionById = buildMentionLookup(mentions, users);

  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  const regex = new RegExp(MENTION_TOKEN_REGEX.source, 'g');
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    const userId = match[1];
    const start = match.index;
    if (start > lastIndex) {
      parts.push(text.slice(lastIndex, start));
    }
    const meta = mentionById.get(userId);
    const displayName = meta?.name ?? userId;
    parts.push(
      <span key={`${start}-${userId}`} className="comment-mention">
        @{displayName}
      </span>
    );
    lastIndex = start + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? parts : [text];
};

/** Backward compatibility for comments created before @[userId] tokens. */
const renderLegacyNameMentions = (
  text: string,
  mentions: MentionMeta[],
  users: Array<{id: string; name: string}>
): React.ReactNode[] => {
  const source = mentions.length
    ? mentions
    : users.map((u) => ({id: u.id, name: u.name}));
  const sorted = [...source].sort((a, b) => b.name.length - a.name.length);
  if (!sorted.length) return [text];

  const patterns = sorted
    .map((u) => u.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .join('|');
  const regex = new RegExp(`(@?(?:${patterns}))`, 'g');
  const segments = text.split(regex);

  return segments.map((part, index) => {
    const normalizedPart = part.startsWith('@') ? part.slice(1) : part;
    const matched = sorted.find((u) => u.name === normalizedPart);
    if (matched) {
      return (
        <span key={index} className="comment-mention">
          @{matched.name}
        </span>
      );
    }

    return part;
  });
};
